import { ColorPickerDropdown } from "components/colorPickerDropdown/ColorPickerDropdown";
import { PRESET_COLORS } from "components/colorpicker/PresetColorPicker";
import { difference, inRange, range } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { IProduct } from "redux/reducers/models/product";
import "./productCards.scss";
import { ButtonNew as Button } from "components/buttonNew";
import ProductCard from "./productCard/ProductCard";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import ProductCardSlot from "./productCardSlot/ProductCardSlot";

import { useTranslation } from "react-i18next";
import RightClickMenu from "components/rightClickMenu/RightClickMenu";

interface IProductCardsProps {
  products: IProduct[];
  productsLoaded: boolean;
  onChangeProductPosition: (id: number, position: number) => void;
  onRemoveProduct: (id: number) => void;
  onAddProducts: (availableProductPositions: number[], totalEmptySlotCount: number) => void;
  setProducts: (products: IProduct[]) => void;
}

interface IProductCardsState {
  gridRows: number;
  gridConstructed: boolean;
  selectedProductIds: number[];
  rangeAnchorProductId: number | undefined;
  selectedRows: number[];
}

export const ProductCardsItemTypes = {
  PRODUCT_CARD: "product_card",
};

function normalizeButtonColorKey(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function applyButtonColorToProduct(product: IProduct, color: string): IProduct {
  return {
    ...product,
    meta: product.meta
      ? { ...product.meta, button_color: color }
      : {
          product_id: null,
          facility_id: null,
          has_modifiers: null,
          kitchen_location_id: null,
          kitchen_location_id_2: null,
          kitchen_location_id_3: null,
          preferred_title: null,
          button_color: color,
          price_locked: false,
        },
  };
}

export function ProductCards(props: IProductCardsProps) {
  const productCardsRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const [colorPickerDropdownOpen, setColorPickerDropdownOpen] = useState<boolean>(false);

  const GRID_COL = 6;
  const EXTRA_GRID_ROWS = 3;

  const { products, productsLoaded, onChangeProductPosition, onRemoveProduct, onAddProducts, setProducts } = props;
  const [productCardsState, setProductCardsState] = useState<IProductCardsState>({
    gridRows: undefined,
    gridConstructed: false,
    selectedProductIds: [],
    rangeAnchorProductId: undefined,
    selectedRows: [],
  });

  function getSelectedProduct(id: number) {
    return products.find(product => product.id === id);
  }

  function getRowFromPosition(position: number) {
    return Math.ceil(position / GRID_COL);
  }

  function getRowFromProductId(productId: number) {
    const productPosition = getSelectedProduct(productId)?.folder_position;
    return getRowFromPosition(productPosition);
  }

  function getRowFromIndex(index: number) {
    return Math.ceil((index + 1) / GRID_COL);
  }

  function getFinalProductRow() {
    if (products.length === 0) {
      return 1;
    } else {
      const positions = products.map(product => product.folder_position);
      const finalProductPosition = positions.reduce((prev, next) => (prev < next ? next : prev));
      const finalProductRow = getRowFromPosition(finalProductPosition);

      return finalProductRow;
    }
  }

  function representativeSelectedColor(selectedIds: number[]): string {
    if (selectedIds.length === 0) {
      return "";
    }
    const idSet = new Set(selectedIds);
    const selected = products.filter(p => idSet.has(p.id));
    selected.sort((a, b) => a.folder_position - b.folder_position);
    const distinctKeys = new Set(selected.map(p => normalizeButtonColorKey(p.meta?.button_color)));
    if (distinctKeys.size > 1) {
      return "";
    }
    const onlyKey = [...distinctKeys][0];
    if (onlyKey === "") {
      return "";
    }
    const preset = PRESET_COLORS.find(pc => normalizeButtonColorKey(pc) === onlyKey);
    if (preset !== undefined) {
      return preset;
    }
    const firstRaw = selected.find(p => normalizeButtonColorKey(p.meta?.button_color) === onlyKey)?.meta?.button_color;
    return (firstRaw ?? "").trim();
  }

  function isPositionInASelectedRow(position: number) {
    const row = getRowFromPosition(position);
    return productCardsState.selectedRows.some(selectedRow => selectedRow === row);
  }

  useEffect(() => {
    setProductCardsState(prev => {
      const validIds = prev.selectedProductIds.filter(id => products.some(p => p.id === id));
      const anchorValid =
        prev.rangeAnchorProductId !== undefined && products.some(p => p.id === prev.rangeAnchorProductId);
      const rangeAnchorProductId = anchorValid ? prev.rangeAnchorProductId : undefined;

      let next: IProductCardsState = {
        ...prev,
        selectedProductIds: validIds,
        rangeAnchorProductId,
      };

      if (productsLoaded && !prev.gridConstructed) {
        const finalProductRow = getFinalProductRow();
        next = {
          ...next,
          gridRows: Math.max(Math.ceil(products.length / GRID_COL) + EXTRA_GRID_ROWS, finalProductRow),
          gridConstructed: true,
        };
      }

      if (prev.gridConstructed) {
        const finalProductRow = getFinalProductRow();

        if (finalProductRow > prev.gridRows) {
          next = {
            ...next,
            gridRows: finalProductRow,
          };
        }
      }

      return next;
    });
  }, [products, productsLoaded]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      const clickedElement = target instanceof Element ? target : null;
      const clickedProductTile =
        clickedElement?.closest(".product-card") != null || clickedElement?.closest(".product-card-slot") != null;
      const clickedColorUi =
        clickedElement?.closest(".color-picker-dropdown") != null ||
        clickedElement?.closest(".color-picker-popup") != null;
      const clickedRightClickMenu = clickedElement?.closest(".right-click-menu") != null;
      const willClearSelection = !clickedProductTile && !clickedColorUi && !clickedRightClickMenu;

      if (willClearSelection) {
        setProductCardsState(prev => ({
          ...prev,
          selectedProductIds: [],
          rangeAnchorProductId: undefined,
          selectedRows: [],
        }));
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  function selectRow(selectedRow: number) {
    setProductCardsState(prev => {
      const selectedRows = prev.selectedRows;
      const updatedRows = selectedRows.some(row => row === selectedRow)
        ? selectedRows.filter(row => row !== selectedRow)
        : [...selectedRows, selectedRow];
      return {
        ...prev,
        selectedRows: updatedRows,
      };
    });
  }

  function selectProduct(event: React.MouseEvent<HTMLDivElement>, productId: number) {
    const clicked = getSelectedProduct(productId);
    if (!clicked) {
      return;
    }

    if (event.shiftKey) {
      setProductCardsState(prev => {
        const anchorId = prev.rangeAnchorProductId;
        const anchorProduct = anchorId !== undefined ? products.find(p => p.id === anchorId) : undefined;
        if (!anchorProduct) {
          return {
            ...prev,
            selectedProductIds: [productId],
            rangeAnchorProductId: productId,
          };
        }
        const lo = Math.min(anchorProduct.folder_position, clicked.folder_position);
        const hi = Math.max(anchorProduct.folder_position, clicked.folder_position);
        const ids = products.filter(p => p.folder_position >= lo && p.folder_position <= hi).map(p => p.id);
        return {
          ...prev,
          selectedProductIds: ids,
        };
      });
      return;
    }

    if (event.metaKey || event.ctrlKey) {
      setProductCardsState(prev => {
        const has = prev.selectedProductIds.includes(productId);
        const selectedProductIds = has
          ? prev.selectedProductIds.filter(id => id !== productId)
          : [...prev.selectedProductIds, productId];
        return {
          ...prev,
          selectedProductIds,
          rangeAnchorProductId: productId,
        };
      });
      return;
    }

    setProductCardsState(prev => {
      if (prev.selectedProductIds.length === 1 && prev.selectedProductIds[0] === productId) {
        return {
          ...prev,
          selectedProductIds: [],
          rangeAnchorProductId: undefined,
        };
      }
      return {
        ...prev,
        selectedProductIds: [productId],
        rangeAnchorProductId: productId,
      };
    });
  }

  function selectEmptyCard(event: React.MouseEvent<HTMLDivElement>, position: number) {
    if (event.shiftKey) {
      const selectedRow = getRowFromPosition(position);
      selectRow(selectedRow);
    } else {
      const availablePositions = calculateSubsequentAvailablePositions(position);
      const totalEmptySlotCount = calculateTotalUntakenPositions().length;
      onAddProducts(availablePositions, totalEmptySlotCount);
      setProductCardsState(prev => ({
        ...prev,
        selectedProductIds: [],
        rangeAnchorProductId: undefined,
      }));
    }
  }

  function calculateTotalUntakenPositions() {
    const maxPosition = GRID_COL * productCardsState.gridRows;
    const allPositions = range(1, maxPosition + 1);
    const occupiedPositions = products.map(product => product.folder_position);
    return difference(allPositions, occupiedPositions);
  }

  function calculateSubsequentAvailablePositions(currentPosition: number) {
    const allUntakenPositions = calculateTotalUntakenPositions();
    return allUntakenPositions.filter(position => position >= currentPosition);
  }

  function handleColorDropdownToggle() {
    setColorPickerDropdownOpen(prev => !prev);
  }

  function handleColorChange(color: string) {
    const ids = productCardsState.selectedProductIds;
    if (ids.length === 0) {
      return;
    }
    const idSet = new Set(ids);
    const updatedProducts = products.map(product => {
      if (idSet.has(product.id)) {
        return applyButtonColorToProduct(product, color);
      }
      return { ...product };
    });
    setProducts(updatedProducts);
  }

  function addRow(index?: number) {
    const canUseIndex = index !== undefined;
    const soleSelectedId =
      productCardsState.selectedProductIds.length === 1 ? productCardsState.selectedProductIds[0] : undefined;
    if (!canUseIndex && soleSelectedId === undefined) {
      return;
    }
    const selectedRow = canUseIndex ? getRowFromIndex(index) : getRowFromProductId(soleSelectedId);
    const rowShiftThreshold = selectedRow * GRID_COL;
    const productsBeforeThreshold = products
      .filter(product => product.folder_position <= rowShiftThreshold)
      .map(product => ({ ...product }));
    const productsAfterThreshold = products
      .filter(product => product.folder_position > rowShiftThreshold)
      .map(product => ({ ...product, folder_position: product.folder_position + GRID_COL }));
    const allProducts = [...productsBeforeThreshold, ...productsAfterThreshold];

    setProducts(allProducts);
    setProductCardsState(prev => ({ ...prev, gridRows: prev.gridRows + 1 }));
  }

  function deleteRows(selectedRows?: number[]) {
    let rowsToDelete = 0;
    const updatedProducts: IProduct[] = [];
    const rowsToDeleteArray = selectedRows ?? productCardsState.selectedRows;
    for (let r = 1; r <= productCardsState.gridRows; r++) {
      if (!rowsToDeleteArray.some(selectedRow => selectedRow === r)) {
        const startOfRowPosition = (r - 1) * GRID_COL + 1;
        const endOfRowPosition = r * GRID_COL;
        const updatedRow: IProduct[] = products
          .filter(product => inRange(product.folder_position, startOfRowPosition, endOfRowPosition + 1))
          .map(product => ({ ...product, folder_position: product.folder_position - rowsToDelete * GRID_COL }));

        updatedProducts.push(...updatedRow);
      } else {
        rowsToDelete++;
      }
    }

    setProducts(updatedProducts);
    setProductCardsState(prev => ({ ...prev, gridRows: prev.gridRows - rowsToDelete, selectedRows: [] }));
  }

  function handleEditProduct(product: IProduct) {
    window.open("/admin/product/" + String(product.id));
  }

  if (!productCardsState.gridConstructed) {
    return null;
  } else {
    const selectedColor = representativeSelectedColor(productCardsState.selectedProductIds);

    return (
      <div className="product-cards-container" ref={productCardsRef}>
        <div className="product-cards-action-menu">
          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-500">{t("Right click on a slot to add or delete rows")}</span>
            <ColorPickerDropdown
              isDropdownOpen={colorPickerDropdownOpen}
              setIsDropdownOpen={setColorPickerDropdownOpen}
              onChange={handleColorChange}
              selectedColor={selectedColor}
            >
              <Button onClick={handleColorDropdownToggle} disabled={productCardsState.selectedProductIds.length === 0}>
                {productCardsState.selectedProductIds.length > 1
                  ? `${t("Change Color")} (${productCardsState.selectedProductIds.length})`
                  : t("Change Color")}
              </Button>
            </ColorPickerDropdown>
          </div>
        </div>
        <DndProvider backend={HTML5Backend}>
          <div className="product-cards">
            {range(1, GRID_COL * productCardsState.gridRows + 1).map((position, index) => {
              const product = products.find(product => product.folder_position === position);
              if (product) {
                const isSelected = productCardsState.selectedProductIds.includes(product.id);

                return (
                  <React.Fragment key={product.id}>
                    <RightClickMenu
                      sections={[
                        [
                          {
                            icon: "pen-to-square",
                            title: t("Edit Product"),
                            action: () => handleEditProduct(product),
                          },
                          {
                            icon: "plus",
                            title: t("Add Row"),
                            action: () => addRow(index),
                          },
                          {
                            icon: "trash",
                            title: t("Delete Row"),
                            action: () => deleteRows([getRowFromIndex(index)]),
                          },
                        ],
                      ]}
                    >
                      <ProductCard
                        product={product}
                        isSelected={isSelected}
                        isPositionInASelectedRow={isPositionInASelectedRow}
                        selectProduct={selectProduct}
                        onRemoveProduct={onRemoveProduct}
                        onChangeProductPosition={onChangeProductPosition}
                      />
                    </RightClickMenu>
                  </React.Fragment>
                );
              } else {
                return (
                  <React.Fragment key={String(Date.now()) + String(position)}>
                    <RightClickMenu
                      sections={[
                        [
                          {
                            icon: "plus",
                            title: t("Add Row"),
                            action: () => addRow(index),
                          },
                          {
                            icon: "trash",
                            title: t("Delete Row"),
                            action: () => deleteRows([getRowFromIndex(index)]),
                          },
                        ],
                      ]}
                    >
                      <ProductCardSlot
                        position={position}
                        isPositionInASelectedRow={isPositionInASelectedRow}
                        selectEmptyCard={selectEmptyCard}
                        onChangeProductPosition={onChangeProductPosition}
                      />
                    </RightClickMenu>
                  </React.Fragment>
                );
              }
            })}
          </div>
        </DndProvider>
      </div>
    );
  }
}
